import { every, isEmpty, get } from 'lodash';

import {
  AUTH_DISCARD_USER,
  AUTH_SET_USER,
  AUTH_UNSET_FIRST_LOGIN,
  CHANGE_SHOP,
  IMPERSONATE_USER,
  RESET_PASSWORD,
  REVERSE_USER,
  SET_BARBER_USER,
  SIGN_UP_ERROR,
  SET_FRONTEND_FLAGS,
  AUTH_SET_ADMIN_USER,
  SET_PERMISSION_PASSCODE,
  SET_BRAND,
  RESET_SHOP,
  DISMISS_MAINTENANCE_MESSAGE,
  PERMISSION_PASSCODE_LOADING,
  PERMISSION_PASSCODE_LOADED,
} from './constants';
import {
  UPDATE_SHOP,
  ADD_SHOP_PHOTO,
} from '../main/shops/constants';
import {
  UPDATE_BRAND_SETTINGS,
} from '../main/settings/brandSettings/constants';
import config from '../../config';
import {
  defaultShopPermissions,
  defaultBarberPermissions,
  defaultBrandPermissions,
} from './utils/defaultPermissions';
import { persistentKVStore } from '../../modules/infra/persistence';

const initialState = {
  token: null,
  user: null,
  timezone: config.defaultTimezone,
  dateFormat: config.dateFormat,
  shop: persistentKVStore.hasItem('shop') ? persistentKVStore.getItem('shop') : null,
  signUpError: null,
  resetPasswordResponse: null,
  adminUser: null,
  isUserImpersonated: persistentKVStore.hasItem('adminToken'),
  barberUser: null,
  permissions: {},
  shopKind: null,
  trackEmployeeTime: false,
  commissionSplits: false,
  adminToken: persistentKVStore.hasItem('adminToken') ? persistentKVStore.getItem('adminToken') : null,
  activeShop: null,
  activeType: persistentKVStore.hasItem('activeType') ? persistentKVStore.getItem('activeType') : null,
  shopBrand: null,
  permissionPasscodeLoading: false,
};

function authReducer(state = initialState, action) {
  function updateShop(updatedShop) {
    const user = state.user;
    const shops = state.user.shops;
    user.shops = shops.map(shop => ((shop.id === updatedShop.id) ? updatedShop : shop));
    return user;
  }

  function replaceShopPhoto(shopId, photo) {
    const user = state.user;
    const shops = state.user.shops;
    user.shops = shops.map((shop) => {
      if (shop.id === shopId) {
        const photos = [];
        photos.push(photo);
        shop.photos = photos; // eslint-disable-line no-param-reassign
      }
      return shop;
    });
    return user;
  }

  switch (action.type) {
    case AUTH_SET_USER:
      return Object.assign({}, state, {
        token: action.token,
        user: action.user,
      });
    case AUTH_UNSET_FIRST_LOGIN:
      return {
        ...state,
        user: {
          ...state.user,
          isFirstLogin: false,
        },
      };

    case AUTH_DISCARD_USER:
      return Object.assign({}, state, {
        token: null,
        user: null,
        shop: null,
        barberUser: null,
        shopKind: null,
        commissionSplits: null,
        activeShop: null,
        isUserImpersonated: false,
      });
    case CHANGE_SHOP:
      return Object.assign({}, state, {
        shop: action.shop.id,
        activeShop: action.shop,
        shopKind: action.shop.kind,
        currency: action.shop.currency,
        timezone: (action.shop && action.shop.timezone) || state.timezone,
        dateFormat: action.shop ? action.shop.dateFormat : state.dateFormat,
        permissions: defaultShopPermissions(action.shop.permissions),
        trackEmployeeTime: action.shop.trackEmployeeTime,
        permissionPasscode: action.shop.permissionPasscode,
        permissionPasscodeRequired: action.shop.permissionPasscodeRequired,
        commissionSplits: action.shop.commissionSplits,
        cashDrawerEnabled: action.shop.cashDrawerEnabled,
        payworksProcessor: action.shop.paymentProcessorReader === 'payworks',
        shopBrand: action.shop.brand,
        activeType: 'shop',
      });
    case RESET_SHOP:
      return Object.assign({}, state, {
        timezone: config.defaultTimezone,
        dateFormat: config.dateFormat,
        shop: null,
        permissions: {},
        shopKind: null,
        trackEmployeeTime: false,
        commissionSplits: false,
        activeShop: null,
        cashDrawerEnabled: false,
        payworksProcessor: false,
        activeType: null,
      });
    case SIGN_UP_ERROR:
      return Object.assign({}, state, {
        signUpError: action.signUpError,
      });
    case UPDATE_SHOP:
      return Object.assign({}, state, {
        user: updateShop(action.shop),
        timezone: action.shop.timezone,
        dateFormat: action.shop.dateFormat,
        trackEmployeeTime: action.shop.trackEmployeeTime,
        permissionPasscode: action.shop.permissionPasscode,
        permissionPasscodeRequired: action.shop.permissionPasscodeRequired,
        commissionSplits: action.shop.commissionSplits,
        activeShop: action.shop,
      });
    case ADD_SHOP_PHOTO:
      return Object.assign({}, state, {
        user: replaceShopPhoto(action.shopId, action.photo),
      });
    case RESET_PASSWORD:
      return Object.assign({}, state, {
        resetPasswordResponse: action.payload,
      });
    case IMPERSONATE_USER:
      return Object.assign({}, state, {
        adminUser: state.user,
        isUserImpersonated: true,
        user: action.user,
        token: action.user.token,
        adminToken: state.user.token,
      });
    case REVERSE_USER:
      return Object.assign({}, state, {
        user: state.adminUser,
        isUserImpersonated: false,
        adminUser: null,
        barberUser: null,
        adminToken: null,
        token: state.adminToken,
        trackEmployeeTime: false,
      });
    case SET_BARBER_USER:
      return Object.assign({}, state, {
        barberUser: action.barber,
        timezone: (action.barber.shop && action.barber.shop.timezone) || state.timezone,
        dateFormat: action.barber.shop ? action.barber.shop.dateFormat : state.dateFormat,
        permissions: defaultBarberPermissions(action.barber.permissions),
        trackEmployeeTime: action.barber.trackEmployeeTime,
        commissionSplits: action.barber.shop.commissionSplits,
        activeType: 'barber',
      });
    case SET_FRONTEND_FLAGS:
      return {
        ...state,
        user: {
          ...state.user,
          frontendFlags: action.payload,
        },
      };
    case AUTH_SET_ADMIN_USER:
      return Object.assign({}, state, {
        adminUser: action.user,
      });
    case SET_PERMISSION_PASSCODE:
      return {
        ...state,
        permissionPasscode: action.payload,
      };
    case PERMISSION_PASSCODE_LOADING:
      return {
        ...state,
        permissionPasscodeLoading: true,
      };
    case PERMISSION_PASSCODE_LOADED:
      return {
        ...state,
        permissionPasscodeLoading: false,
      };
    case SET_BRAND: {
      const brandState = {
        ...state,
        permissions: defaultBrandPermissions(get(action, 'brand.brand_users.permissions', {})),
        activeType: 'brand',
        paymentCurrency: action.brand.currency,
        shopBrand: action.brand,
      };
      const shops = state.user ? state.user.shops : [];
      if (isEmpty(shops)) {
        return brandState;
      }

      const [shop] = shops;
      const doShopsHaveSameCurrency = every(shops, { currency: shop.currency });
      const currency = doShopsHaveSameCurrency ? shop.currency : 'MIXED';

      const brandFirstShop = (state.user && state.user.firstShop) || {};
      return {
        ...brandState,
        currency,
        timezone: brandFirstShop.timezone || config.defaultTimezone,
        dateFormat: shop.dateFormat,
        activeShop: null,
      };
    }
    case UPDATE_BRAND_SETTINGS: {
      const brandState = {
        ...state.user.brand,
        ...action.brand,
      };
      return {
        ...state,
        user: {
          ...state.user,
          brand: brandState,
        },
        shopBrand: brandState,
      };
    }
    case DISMISS_MAINTENANCE_MESSAGE:
      return {
        ...state,
        user: {
          ...state.user,
          maintenanceDismissed: true,
        },
      };
    default:
      return state;
  }
}

export default authReducer;
